import { ColumnDef } from '@tanstack/react-table';
import { FULFILMENT_STATUS_OPTIONS } from '@/modules/service-requests/shared/application-utils';
import {
  actionsColumn,
  appliedDateColumn,
  departmentColumn,
  DepartmentOption,
  statusColumn,
  textColumn,
} from '@/modules/service-requests/shared/components/column-helpers';
import { StatusSelectCell } from '@/modules/service-requests/shared/components/status-select-cell';
import { canEditBusinessCard, isBusinessCardStatusLocked } from '../../utils/business-card-rules';
import { BusinessCardApplication, BusinessCardStatus } from '../../types/business-card.types';

type Translate = (key: string) => string;

const STATUSES = FULFILMENT_STATUS_OPTIONS.map((option) => option.value);

interface MyColumnsProps {
  t: Translate;
  canApply: boolean;
  canWithdraw: boolean;
  onEdit: (application: BusinessCardApplication) => void;
  onWithdraw: (application: BusinessCardApplication) => void;
}

export const createMyBusinessCardColumns = ({
  t,
  canApply,
  canWithdraw,
  onEdit,
  onWithdraw,
}: MyColumnsProps): ColumnDef<BusinessCardApplication>[] => [
  // Card: the designation is the title and the actions menu the top right; the body runs
  // Request Date | Status, Name | Email, Mobile.
  {
    ...appliedDateColumn<BusinessCardApplication>(t, 'REQUEST_DATE'),
    meta: { filter: { type: 'datetime' }, mobile: 'body', mobileOrder: 0 },
  },
  textColumn<BusinessCardApplication>(t, 'Designation', 'DESIGNATION', (row) => row.Designation, {
    mobile: 'title',
  }),
  textColumn<BusinessCardApplication>(t, 'Name', 'NAME', (row) => row.Name, {
    mobile: 'body',
    mobileOrder: 2,
  }),
  textColumn<BusinessCardApplication>(t, 'Email', 'EMAIL', (row) => row.Email, {
    mobileOrder: 3,
  }),
  textColumn<BusinessCardApplication>(
    t,
    'MobileNumber',
    'MOBILE_NUMBER',
    (row) => row.MobileNumber,
    { mobileOrder: 4 }
  ),
  (() => {
    const column = statusColumn<BusinessCardApplication>(t, STATUSES);
    return { ...column, meta: { ...column.meta, mobile: 'body' as const, mobileOrder: 1 } };
  })(),
  actionsColumn<BusinessCardApplication>(t, (application) => [
    {
      label: t('EDIT'),
      onSelect: () => onEdit(application),
      hidden: !canApply || !canEditBusinessCard(application),
    },
    {
      label: t('WITHDRAW_BUSINESS_CARD'),
      onSelect: () => onWithdraw(application),
      hidden: !canWithdraw || application.Status !== 'Pending',
      destructive: true,
    },
  ]),
];

interface RequestColumnsProps {
  t: Translate;
  /** The departments the reviewer may see, for the Department column and its filter. */
  departments: readonly DepartmentOption[];
  canUpdateStatus: boolean;
  /** The application whose status change is being saved; its select is disabled meanwhile. */
  savingId?: string;
  onChangeStatus: (application: BusinessCardApplication, status: BusinessCardStatus) => void;
}

export const createBusinessCardRequestColumns = ({
  t,
  departments,
  canUpdateStatus,
  savingId,
  onChangeStatus,
}: RequestColumnsProps): ColumnDef<BusinessCardApplication>[] => [
  // Card: the request date is the title and the status the top right; the body runs
  // Name | Department, Email | Mobile, with the designation in the footer.
  {
    ...appliedDateColumn<BusinessCardApplication>(t, 'REQUESTED_ON'),
    meta: { filter: { type: 'datetime' }, mobile: 'title' },
  },
  textColumn<BusinessCardApplication>(t, 'Name', 'NAME', (row) => row.Name, {
    mobile: 'body',
    mobileOrder: 0,
  }),
  textColumn<BusinessCardApplication>(t, 'Designation', 'DESIGNATION', (row) => row.Designation, {
    mobile: 'footer',
  }),
  (() => {
    const column = departmentColumn<BusinessCardApplication>(t, departments);
    return { ...column, meta: { ...column.meta, mobile: 'body' as const, mobileOrder: 1 } };
  })(),
  textColumn<BusinessCardApplication>(
    t,
    'MobileNumber',
    'MOBILE_NUMBER',
    (row) => row.MobileNumber,
    { mobile: 'body', mobileOrder: 3 }
  ),
  textColumn<BusinessCardApplication>(t, 'Email', 'EMAIL', (row) => row.Email, {
    mobile: 'body',
    mobileOrder: 2,
  }),
  statusColumn<BusinessCardApplication>(t, STATUSES, ({ row }) => (
    <StatusSelectCell
      status={row.original.Status}
      options={FULFILMENT_STATUS_OPTIONS}
      locked={!canUpdateStatus || isBusinessCardStatusLocked(row.original)}
      disabled={row.original.ItemId === savingId}
      onChange={(status) => onChangeStatus(row.original, status as BusinessCardStatus)}
    />
  )),
];
